import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { UserRole } from '@/types/user';
import { hasPermission, PERMISSIONS } from '@/utils/permissions';

interface RoleBasedRouteProps {
  children: React.ReactNode;
  requiredRole?: UserRole;
  requiredRoles?: UserRole[];
  requiredPermission?: keyof typeof PERMISSIONS;
  requiredPermissions?: (keyof typeof PERMISSIONS)[];
  fallbackPath?: string;
  showForbiddenMessage?: boolean;
}

const RoleBasedRoute: React.FC<RoleBasedRouteProps> = ({
  children,
  requiredRole,
  requiredRoles,
  requiredPermission,
  requiredPermissions,
  fallbackPath,
  showForbiddenMessage = true,
}) => {
  const { user, loading, getDashboardRoute } = useAuth();
  const location = useLocation();

  // Show loading while checking authentication
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-sauna-blue"></div>
      </div>
    );
  }

  // If not authenticated, redirect to login
  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Check role-based access
  if (requiredRole && user.role !== requiredRole && !user.roles?.includes(requiredRole)) {
    if (showForbiddenMessage) {
      return (
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-red-600 mb-4">Access Denied</h1>
            <p className="text-gray-600 mb-4">
              You don't have the required role to access this page.
            </p>
            <button
              onClick={() => window.history.back()}
              className="px-4 py-2 bg-sauna-blue text-white rounded hover:bg-sauna-blue/90"
            >
              Go Back
            </button>
          </div>
        </div>
      );
    }
    return <Navigate to={fallbackPath || getDashboardRoute()} replace />;
  }

  // Check multiple roles
  if (requiredRoles && requiredRoles.length > 0) {
    const hasRequiredRole = requiredRoles.some(role => 
      user.role === role || user.roles?.includes(role)
    );
    
    if (!hasRequiredRole) {
      if (showForbiddenMessage) {
        return (
          <div className="flex items-center justify-center min-h-screen">
            <div className="text-center">
              <h1 className="text-2xl font-bold text-red-600 mb-4">Access Denied</h1>
              <p className="text-gray-600 mb-4">
                You don't have the required role to access this page.
              </p>
              <button
                onClick={() => window.history.back()}
                className="px-4 py-2 bg-sauna-blue text-white rounded hover:bg-sauna-blue/90"
              >
                Go Back
              </button>
            </div>
          </div>
        );
      }
      return <Navigate to={fallbackPath || getDashboardRoute()} replace />;
    }
  }

  // Check permission-based access
  if (requiredPermission && !hasPermission(user, requiredPermission)) {
    if (showForbiddenMessage) {
      return (
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-red-600 mb-4">Access Denied</h1>
            <p className="text-gray-600 mb-4">
              You don't have the required permission to access this page.
            </p>
            <button
              onClick={() => window.history.back()}
              className="px-4 py-2 bg-sauna-blue text-white rounded hover:bg-sauna-blue/90"
            >
              Go Back
            </button>
          </div>
        </div>
      );
    }
    return <Navigate to={fallbackPath || getDashboardRoute()} replace />;
  }

  // Check multiple permissions
  if (requiredPermissions && requiredPermissions.length > 0) {
    const hasRequiredPermission = requiredPermissions.some(permission => 
      hasPermission(user, permission)
    );
    
    if (!hasRequiredPermission) {
      if (showForbiddenMessage) {
        return (
          <div className="flex items-center justify-center min-h-screen">
            <div className="text-center">
              <h1 className="text-2xl font-bold text-red-600 mb-4">Access Denied</h1>
              <p className="text-gray-600 mb-4">
                You don't have the required permissions to access this page.
              </p>
              <button
                onClick={() => window.history.back()}
                className="px-4 py-2 bg-sauna-blue text-white rounded hover:bg-sauna-blue/90"
              >
                Go Back
              </button>
            </div>
          </div>
        );
      }
      return <Navigate to={fallbackPath || getDashboardRoute()} replace />;
    }
  }

  // If all checks pass, render the children
  return <>{children}</>;
};

// Convenience components for common role checks
export const AdminRoute: React.FC<{ children: React.ReactNode; fallbackPath?: string }> = ({ 
  children, 
  fallbackPath 
}) => {
  const { user } = useAuth();
  
  // Debug logging
  console.log('🔍 AdminRoute Debug:', {
    user: user ? {
      id: user.id,
      email: user.email,
      role: user.role,
      roles: user.roles
    } : null,
    hasRole: user ? user.role === 'admin' || user.roles?.includes('admin') : false
  });
  
  return (
    <RoleBasedRoute requiredRole="admin" fallbackPath={fallbackPath}>
      {children}
    </RoleBasedRoute>
  );
};

export const OperatorRoute: React.FC<{ children: React.ReactNode; fallbackPath?: string }> = ({ 
  children, 
  fallbackPath 
}) => (
  <RoleBasedRoute requiredRoles={['operator', 'admin']} fallbackPath={fallbackPath}>
    {children}
  </RoleBasedRoute>
);

export const GuestRoute: React.FC<{ children: React.ReactNode; fallbackPath?: string }> = ({ 
  children, 
  fallbackPath 
}) => (
  <RoleBasedRoute requiredRoles={['guest', 'operator', 'admin']} fallbackPath={fallbackPath}>
    {children}
  </RoleBasedRoute>
);

export const AuthenticatedRoute: React.FC<{ children: React.ReactNode; fallbackPath?: string }> = ({ children, fallbackPath }) => {
  return (
    <RoleBasedRoute requiredRoles={['guest', 'operator', 'admin']} fallbackPath={fallbackPath}>
      {children}
    </RoleBasedRoute>
  );
};

export default RoleBasedRoute; 